/**
 * photonicCrystalPipeline — a periodic dielectric structure with
 * photonic band gaps.
 *
 * A photonic crystal is a periodic dielectric structure that creates
 * a "band gap" — a range of wavelengths that cannot propagate. We
 * render the crystal as a 2D lattice of circular holes in a high-
 * index medium, and light propagates as wavefronts that refract
 * and diffract at the interfaces.
 *
 * Innovations:
 *   - The lattice is drawn as a hexagonal close-packed array of
 *     circular holes, each refracting light according to its local
 *     "permittivity" (a function of lattice position).
 *   - Light is modeled as incoming wavefronts that bend around
 *     the holes. We compute the wavefront propagation by sampling
 *     the cosine of the parallel distance to each hole.
 *   - The "band gap" is shown as a central region where propagating
 *     waves are attenuated — a dark vertical band.
 *   - Defect modes: a missing hole in the lattice creates a "cavity"
 *     that traps light at a specific frequency, visible as a bright
 *     spot.
 *   - Audio: bass shifts the wave frequency, mid perturbs the
 *     lattice, high drives the propagating wave brightness.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PhotonicCrystalPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — dark substrate.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.08, 0.10, 0.18),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Hexagonal lattice of holes.
  float latticeA = 0.10;  // lattice constant
  vec2 latticeA2 = vec2(latticeA * sqrt(3.0), latticeA * 1.5);
  float holeR = 0.04 + 0.005 * mid;

  // Hex grid coordinates.
  vec2 q = vec2(p.x / latticeA, p.y / (latticeA * 0.866));
  float col_x = floor(q.x);
  float col_y = floor(q.y);
  float off_x = q.x - col_x;
  float off_y = q.y - col_y;
  // Check 3 nearest cells (hexagonal).
  float dist = 1e9;
  vec2 closestHole = vec2(0.0);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 cellOff = vec2(float(i), float(j));
      // Hex offset: even rows are shifted.
      float isOdd = mod(col_y, 2.0);
      float xShift = isOdd * 0.5;
      vec2 holePos = vec2(
        (col_x + cellOff.x + 0.5 + xShift) * latticeA,
        (col_y + cellOff.y + 0.5) * latticeA * 0.866
      );
      // Skip the "defect" hole at the center.
      float dx = p.x - holePos.x;
      float dy = p.y - holePos.y;
      float d = sqrt(dx * dx + dy * dy);
      if (d < dist) {
        dist = d;
        closestHole = holePos;
      }
    }
  }

  // Draw the holes (white circles — light passes through them).
  float holeMask = 1.0 - smoothstep(holeR - 0.005, holeR, dist);
  col += vec3(0.4, 0.7, 1.0) * holeMask * 0.7;
  // Hole edges.
  float holeEdge = 1.0 - smoothstep(0.0, 0.005, abs(dist - holeR));
  col += vec3(0.95, 0.95, 0.7) * holeEdge * 0.6;

  // Incoming wavefronts — plane waves from the left, propagating right.
  float k = 20.0 + 10.0 * bass;  // wave number
  float omega = 4.0 + 3.0 * bass;
  float wave = cos(k * p.x - omega * uTime);
  // Wave amplitude depends on whether the wave is in the crystal.
  // The wave is attenuated in the "band gap" region (the central area).
  float bandGap = exp(-abs(p.y) * 3.0);
  wave *= 1.0 - bandGap * 0.9;
  // Wave diffraction near holes.
  float phaseShift = 0.0;
  float closeHole = 1e9;
  vec2 closestHoleVis = vec2(0.0);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 cellOff = vec2(float(i), float(j));
      float isOdd = mod(col_y, 2.0);
      float xShift = isOdd * 0.5;
      vec2 holePos = vec2(
        (col_x + cellOff.x + 0.5 + xShift) * latticeA,
        (col_y + cellOff.y + 0.5) * latticeA * 0.866
      );
      float dx = p.x - holePos.x;
      float dy = p.y - holePos.y;
      float d = sqrt(dx * dx + dy * dy);
      if (d < closeHole) {
        closeHole = d;
        closestHoleVis = holePos;
      }
    }
  }
  // Phase shift from the nearest hole.
  float phase = k * closestHoleVis.x - omega * uTime;
  float diffract = cos(phase + closeHole * 5.0) * exp(-closeHole * 8.0);

  // Combine the propagating wave + diffraction.
  float waveField = wave * 0.4 + diffract * 0.6;
  vec3 waveColor = vec3(0.4, 0.85, 0.95);
  col += waveColor * waveField * 0.3 * (0.5 + 0.5 * high);

  // Defect cavity — a brighter spot where the lattice is broken.
  float defectY = 0.0;
  float defectX = -0.55;
  float defectD = length(p - vec2(defectX, defectY));
  float defect = exp(-defectD * 8.0) * (0.7 + 0.3 * sin(uTime * 4.0));
  col += vec3(1.0, 0.95, 0.7) * defect * 0.8;

  // Band gap visualization — a darker vertical band where waves can't propagate.
  float band = smoothstep(0.0, 0.30, abs(p.y)) * 0.4;
  col *= 1.0 - band;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computePhotonicCrystalBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computePhotonicCrystalRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPhotonicCrystalPipeline(gl: WebGL2RenderingContext): PhotonicCrystalPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPhotonicCrystal(
  gl: WebGL2RenderingContext,
  pipe: PhotonicCrystalPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
